import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import {
  Bell, Mail, Trash2, CalendarClock, TrendingDown, Trophy, AlertOctagon,
  Home, ZapOff, Target, TrendingUp,
} from "lucide-react";
import { formatRelativeShortEs } from "@/lib/relativeTime";

const TYPE_META = {
  due_date: { icon: CalendarClock, tone: "text-[#C89A3F]", bg: "bg-[#C89A3F]/10", label: "Vencimiento" },
  overdue: { icon: AlertOctagon, tone: "text-[#D95D39]", bg: "bg-[#D95D39]/10", label: "Vencido" },
  unusual_expense: { icon: TrendingUp, tone: "text-[#8B6B1F]", bg: "bg-[#8B6B1F]/10", label: "Gasto inusual" },
  revaluation: { icon: Home, tone: "text-[#1C7C54]", bg: "bg-[#1C7C54]/10", label: "Revalorización" },
  stale_price: { icon: ZapOff, tone: "text-[#5F7368]", bg: "bg-[#5F7368]/10", label: "Cotización" },
  all_time_high: { icon: Trophy, tone: "text-[#1C7C54]", bg: "bg-[#1C7C54]/10", label: "Máximo" },
  sharp_drop: { icon: TrendingDown, tone: "text-[#D95D39]", bg: "bg-[#D95D39]/10", label: "Caída fuerte" },
  price_target: { icon: Target, tone: "text-[#8B6B1F]", bg: "bg-[#8B6B1F]/10", label: "Objetivo" },
  email_digest: { icon: Mail, tone: "text-[#1C7C54]", bg: "bg-[#1C7C54]/10", label: "Email diario" },
};

const DEFAULT_META = { icon: Bell, tone: "text-[#5F7368]", bg: "bg-[#EBEBEA]", label: "Notificación" };

export default function NotificationHistoryDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications/history", { params: { limit: 50 } });
      setItems(r.data?.items || []);
    } catch (e) {
      logger.error("history load", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const clear = async () => {
    if (!window.confirm("¿Borrar todo el historial de notificaciones?")) return;
    setClearing(true);
    try {
      const r = await api.delete("/notifications/history");
      toast.success(`Historial borrado (${r.data?.deleted ?? 0})`);
      setItems([]);
    } catch {
      toast.error("No se pudo borrar");
    } finally {
      setClearing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#1C7C54]" />
            Historial de alertas
          </DialogTitle>
          <p className="text-xs text-[#8A9992]">
            Últimas 50 notificaciones enviadas (push + email).
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="notif-history-list">
          {loading ? (
            <div className="p-6 text-sm text-[#5F7368]">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#8A9992]" data-testid="notif-history-empty">
              <Bell className="w-8 h-8 mx-auto text-[#EBEBEA] mb-2" />
              <div>Todavía no se ha enviado ninguna alerta.</div>
              <div className="text-xs mt-1">
                Aparecerán aquí las notificaciones push y los emails resumidos.
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-[#EBEBEA]">
              {items.map((it, idx) => {
                const meta = TYPE_META[it.type] || DEFAULT_META;
                const Icon = meta.icon;
                const isEmail = it.channel === "email";
                // Stable key: rely on `key` (the alert dedupe id) with `at` as
                // secondary uniqueness for the rare case two rows share a key.
                const rowKey = `${it.key}-${it.at}`;
                return (
                  <li
                    key={rowKey}
                    data-testid={`notif-history-item-${idx}`}
                    className="py-3 flex items-start gap-3"
                  >
                    <div className={`w-9 h-9 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${meta.tone}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#0A110E] truncate">
                          {it.title}
                        </span>
                        <span className={`text-[10px] uppercase tracking-[0.06em] font-bold px-1.5 py-0.5 rounded-full ${
                          isEmail
                            ? "bg-[#1C7C54]/10 text-[#1C7C54]"
                            : "bg-[#0F291E]/10 text-[#0F291E]"
                        }`}>
                          {isEmail ? "email" : "push"}
                        </span>
                      </div>
                      <div className="text-xs text-[#5F7368] mt-0.5 line-clamp-2">
                        {it.body}
                      </div>
                      <div className="text-[11px] text-[#8A9992] mt-1 flex items-center gap-2">
                        <span>{it.at ? formatRelativeShortEs(it.at) : "—"}</span>
                        {isEmail && it.recipient && (
                          <span className="truncate">· {it.recipient}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            onClick={clear}
            disabled={clearing || items.length === 0}
            data-testid="notif-history-clear-btn"
            className="text-[#D95D39] hover:text-[#D95D39] hover:bg-[#D95D39]/10"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {clearing ? "Borrando…" : "Borrar todo"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
